import { useCallback, useMemo, useRef } from "react";
import {
  atom,
  atomFamily,
  selectorFamily,
  useRecoilCallback,
  useRecoilState,
  useRecoilTransaction_UNSTABLE,
  useRecoilValue,
  useSetRecoilState
} from "recoil";
import axios from "axios";
import deepmerge from "deepmerge";

import WRAP from "WRAP-JS";
import {
  formatTimeZoneDate,
  getProperty,
  getRoutePerspective,
  isEmpty,
  isEqualObject,
  parseUTCDate,
  to360degreesRange
} from "../utils/FPEUtils";
import { useLayoutState } from "./LayoutState";
import { useCrossSectionData } from "./CrossSectionDataState";
import { nearValidTime } from "../components/map/helper/MapUtil";
import { configModelGroundForFP, configModelUpperForFP } from "../components/map/layers/config/ConfigModelForFP";
import { userState } from "./UserState";
import { mapPerspectiveState } from "./MapPerspectiveState";
import { dhConfigGassClingForFp, dhConfigGassHvisForFp } from "../components/map/layers/config/ConfigGassScaleForFp";
import { configElevationDh } from "../components/map/layers/config/ConfigElevation";
import { useAlert } from "../hooks/useAlert";
import { removeDuplicateMessagesSelector } from "./AlertState";
import {
  COMMUNICATE_ERROR_MESSAGE_ELEVATION_DATA,
  COMMUNICATE_ERROR_MESSAGE_GASS_SCALE_CLING,
  COMMUNICATE_ERROR_MESSAGE_GASS_SCALE_HVIS,
  COMMUNICATE_ERROR_MESSAGE_LINKS_AIRCRAFT_LIST,
  COMMUNICATE_ERROR_MESSAGE_SUNRISE_DATA,
  COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER,
  COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD,
  COMMUNICATE_ERROR_MESSAGE_WNI_SRF_VSCAL_GASS_CLING,
  COMMUNICATE_ERROR_MESSAGE_WNI_SRF_VSCAL_GASS_HVIS
} from "../constants/ErrorMessage";
import { makeErrorMessage } from "../utils/ErrorMessageUtil";
import { dhConfigCalendarSun } from "../components/map/layers/config/ConfigCalendar";
import { timeZoneSelector } from "./TimeZoneState";

const _listAircraftCode = async (userGroup) => {
  const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
  const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
  const config = {
    method: 'get',
    url: `${apiUrl}/links/aircraft_list`,
    params: {
      'usergroup_id': userGroup
    },
    headers: {
      'x-api-key': apiKey
    },
    errorHandle: {
      message: COMMUNICATE_ERROR_MESSAGE_LINKS_AIRCRAFT_LIST,
      additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
    }
  };

  const data = axios(config)
    .then(response => {
      // console.log(JSON.stringify(response.data));
      const list = response.data || [];
      return list;
    })
    .catch(e => {
      console.log('Failed to retrieve aircraft code list.', e);
      return [];
    });
  return data;
}

/* フライトプラン詳細画面 機体番号リストstate */
export const aircraftCodeListState = atomFamily({
  key: 'aircraftCodeListState',
  default: selectorFamily({
    key: 'aircraftCodeListSelector',
    get: (userGroup) => () => {
      return _listAircraftCode(userGroup);
    }
  })
});

/* フライトプラン情報取得済state */
const flightPlanInitializedState = atom({
  key: 'flightPlanInitializedState',
  default: false
});

/* フライトプランリストstate */
const flightPlanListState = atom({
  key: 'flightPlanListState',
  default: []
});

/* フライトプラン詳細画面表示対象state */
const flightPlanDetailTargetState = atom({
  key: 'flightPlanDetailTargetState',
  default: {
    customerCode: '',
    skd: '',
    flightNo: '',
    flightPlanId: ''
  }
});

/* フライトプラン編集状態state */
const flightPlanEditState = atom({
  key: 'flightPlanEditState',
  default: {
    editing: false,
    edited: false,
    hasTemporaryPoint: false,
    data: ''
  }
});

/* フライトプラン断面図表示対象state */
const flightPlanCsTargetState = atom({
  key: 'flightPlanCsTargetState',
  default: {
    customerCode: '',
    skd: '',
    flightNo: '',
    flightPlanId: ''
  }
});

/* ハイライトフライトプランstate */
const highlightFlightPlanState = atom({
  key: 'highlightFlightPlanState',
  default: {
    source: '',
    plan: {},
  },
});

/* 気象日付の調整値State */
export const wxDateAdjustmentState = atom({
  key: 'wxDateAdjustmentState',
  default: 0
});

/* Modelの選択状態State */
const selectedModelTypeState = atom({
  key: 'selectedModelState',
  default: ''
});

/* MSM/LFMのデータ取得中trueになる */
const modelLoadingState = atom({
  key: 'modelLoadingState',
  default: false
})

/* ModelのValidtime */
const modelValidtimeState = atom({
  key: 'modelValidtimeState',
  default: null
});

/* Modelのデータ */
const modelPointDataState = atom({
  key: 'modelPointDataState',
  default: []
});

/* フライトプランの再取得を要求するフラグ */
const flightPlanRefreshState = atom({
  key: 'flightPlanRefreshState',
  default: false
});

/* ドラッグ対象地点情報state */
const draggingPointState = atom({
  key: 'draggingPointState',
  default: null
});

/*GASSの選択状態*/
const gassScaleVisibleState = atom({
  key: 'gassScaleVisibleState',
  default: false
});

/*GASSのデータ取得中trueになる*/
const gassDataLoadingState = atom({
  key: 'gassDataLoadingState',
  default: false
})
/*ルートのGASSデータ(HVIS,CLING,Elevation)*/
const gassPointDataState = atom({
  key: 'gassPointData',
  default: {}
});

/* GEOGの選択状態 */
const geographyVisibleState = atom({
  key: 'geographyVisibleState',
  default: false
});

/*GEOGのデータ取得中trueになる*/
const geographyDataLoadingState = atom({
  key: 'geographyDataLoadingState',
  default: false
});

/* ルートのGEOGデータ(Elevation, Sunrise, Sunset) */
const geographyPointDataState = atom({
  key: 'geographyPointDataState',
  default: {}
});

/* DISTの選択状態 */
const distanceVisibleState = atom({
  key: 'distanceVisibleState',
  default: false
});


const flightPlanLoadErrorState = atom({
  key: 'flightPlanLoadState',
  default: false
});

const msmGround = 'JMA_SRF_MSM';
const msmUpper = 'JMA_SRF_MSM_UPPER';
const lfmGround = 'JMA_SRF_LFM';
const lfmUpper = 'JMA_SRF_LFM_UPPER';
const gsmGround = 'JMA_MRF_GSM_JP';
const gsmUpper = 'JMA_MRF_GSM_JP_UPPER';
const gfs = 'NCEP_MRF_GFS_0p25';
const models = {
  [msmGround]: new WRAP.Data(configModelGroundForFP),
  [msmUpper]: new WRAP.Data(configModelUpperForFP),
  [lfmGround]: new WRAP.Data(configModelGroundForFP),
  [lfmUpper]: new WRAP.Data(configModelUpperForFP),
  [gsmGround]: new WRAP.Data(configModelGroundForFP),
  [gsmUpper]: new WRAP.Data(configModelUpperForFP),
  [gfs]: new WRAP.Data(configModelGroundForFP),
};

//WRAPInitializerより先に動くので使うタイミングで初期化する
let dhGassScaleHvis , dhGassScaleCling, dhElevation, dhCalendar;
const initWrapDh = (dispatch) => {
  if (!dhGassScaleHvis) {
    dhGassScaleHvis = new WRAP.Data(dhConfigGassHvisForFp);

    // validtimeをloadするとここがコールバックされるのでエラーハンドリングを行う
    dhGassScaleHvis.updated('timelist', () => {
      dispatch({type: 'success', payload: {key: 'hvis'}});
    }, () => {
      dispatch({
        type: 'error', payload: {
          key: 'hvis', message: {
            message: COMMUNICATE_ERROR_MESSAGE_WNI_SRF_VSCAL_GASS_HVIS,
            additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
          }
        }
      });
    });
  }
  if (!dhGassScaleCling) {
    dhGassScaleCling = new WRAP.Data(dhConfigGassClingForFp);

    // validtimeをloadするとここがコールバックされるのでエラーハンドリングを行う
    dhGassScaleCling.updated('timelist', () => {
      dispatch({type: 'success', payload: {key: 'cling'}});
    }, () => {
      dispatch({
        type: 'error', payload: {
          key: 'cling', message: {
            message: COMMUNICATE_ERROR_MESSAGE_WNI_SRF_VSCAL_GASS_CLING,
            additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
          }
        }
      });
    });
  }
  if (!dhElevation) {
    dhElevation = new WRAP.Data(configElevationDh);
  }
  if (!dhCalendar) {
    dhCalendar = new WRAP.Data(dhConfigCalendarSun);
  }
  return {
    dhGassScaleHvis,
    dhGassScaleCling,
    dhElevation,
    dhCalendar
  };
};

const useWrapDh = () => {
  const {showErrorWithMessage, clearErrorMessage} = useErrorMessage();
  const errorMessageInfo = useRef({});

  // ディスパッチされたエラー情報（発生・解除）に従ってエラーダイアログの表示・解除を行う
  const dispatcher = useCallback((data) => {
    if (data.type === 'success') {
      if (errorMessageInfo.current[data.payload.key]) {
        clearErrorMessage(errorMessageInfo.current[data.payload.key]);
        errorMessageInfo.current[data.payload.key] = null;
      }
    } else if (data.type === 'error') {
      errorMessageInfo.current[data.payload.key] = showErrorWithMessage(data.payload.message);
    }
  }, [showErrorWithMessage, clearErrorMessage]);

  const {dhGassScaleHvis, dhGassScaleCling, dhElevation, dhCalendar} = useMemo(() => {
    return initWrapDh(dispatcher);
    //eslint-disable-next-line
  }, []);

  return {
    dhGassScaleHvis,
    dhGassScaleCling,
    dhElevation,
    dhCalendar
  };
};

export const useFlightPlan = () => {
  const [fpList, setFpList] = useRecoilState(flightPlanListState);
  const [detailTarget, setDetailTarget] = useRecoilState(flightPlanDetailTargetState);
  const [editState, setEditState] = useRecoilState(flightPlanEditState);
  const [csTarget, setCsTarget] = useRecoilState(flightPlanCsTargetState);
  const {isPlanType, setCsTypePlan, clearCsType} = useCrossSectionData();
  const [highlightPlan, setHighlightFlightPlan] = useRecoilState(highlightFlightPlanState);
  const { isMapOnly, changeHorizontal, changeMapOnly } = useLayoutState();
  const [wxDateAdjust, setWxDateAdjust] = useRecoilState(wxDateAdjustmentState);
  const [planInitialized, setFlightPlanInitialized] = useRecoilState(flightPlanInitializedState);
  const [needsRefreshPlan, setFlightPlanRefresh] = useRecoilState(flightPlanRefreshState);
  const [draggingPoint, setDraggingPoint] = useRecoilState(draggingPointState);
  const [flightPlanLoadError, setFlightPlanLoadError] = useRecoilState(flightPlanLoadErrorState);
  const user = useRecoilValue(userState);
  const {setDetail} = useFlightPlanDetail();

  const getFlightPlan = useCallback((target) => {
    if (editState.editing && isEqualFlightPlan(editState.data, target)) {
      return editState.data;
    }
    return searchFlightPlan(fpList, target) || '';
  }, [editState, fpList]);

  const detail = getFlightPlan(detailTarget);
  const cs = getFlightPlan(csTarget);

  const visibleSetting = useMemo(() => {
    const setting = {};
    fpList.forEach((fp) => {
      setting[fp.flightPlanId] = "visible" in fp ? fp.visible : true;
    });
    return setting;
  }, [fpList]);

  const clearCrossSection = useCallback(() => {
    clearCsType();
    setCsTarget({
      customerCode: '',
      skd: '',
      flightNo: '',
      flightPlanId: ''
    });
    if (!isMapOnly) {
      changeMapOnly();
    }
  }, [clearCsType, setCsTarget, isMapOnly, changeMapOnly]);

  const setFlightPlanList = useCallback((list) => {
    const newList = [];
    list.forEach(d => {
      newList.push({
        ...d,
        visible: d.flightPlanId in visibleSetting ? visibleSetting[d.flightPlanId] : true
      });
    });
    setFpList(newList);

    //編集中ではない場合
    if (!editState.editing) {
      //詳細画面に表示していたデータが消えた場合
      if (searchFlightPlan(newList, detailTarget) === null) {
        //detailTargetを初期化
        setDetailTarget({
          customerCode: '',
          skd: '',
          flightNo: '',
          flightPlanId: ''
        });
        setWxDateAdjust(0);
      }

      //断面図に表示していたデータが消えた場合
      if (isPlanType && searchFlightPlan(newList, csTarget) === null) {
        clearCrossSection();
      }
    }
  }, [visibleSetting, setFpList, editState, detailTarget, setDetailTarget, setWxDateAdjust, csTarget, isPlanType, clearCrossSection]);

  const createFlightPlanStart = useCallback(() => {
    if (editState.editing) {
      // console.log("skip startEdit(already started)");
      return;
    }

    const dt = new WRAP.Core.DateTime();
    //TODO SKDは日本時間固定
    const skd = dt.format('YYYY/MM/DD', 'Asia/Tokyo');

    //ETDを切り上げ正時とする
    if (dt.date.getHours() < 23) {
      dt.date.setHours(dt.date.getHours() + 1);
    } else {
      dt.date.setHours(0);
    }
    dt.date.setMinutes(0);
    dt.date.setSeconds(0);
    dt.date.setMilliseconds(0);
    const etd = dt.format('YYYY-MM-DD[T]HH:mm:00', 'utc');
    //TODO ETD(LT)は日本時間固定
    const etdLt = dt.format('YYYY-MM-DD[T]HH:mm:00', 'Asia/Tokyo');

    //ETAはETD+1hとする
    dt.add(60 * 60);
    const eta = dt.format('YYYY-MM-DD[T]HH:mm:ss', 'utc')
    //TODO ETA(LT)は日本時間固定
    const etaLt = dt.format('YYYY-MM-DD[T]HH:mm:ss', 'Asia/Tokyo');

    const mainOp = {
      customerCode: user.customer,
      skd,
      etd,
      eta
    };
    const subOp = {
      etdLt,
      etaLt,
    };
    const data = _initFlightPlan(mainOp, subOp);
    setDetailTarget({
      customerCode: data.customerCode,
      skd: data.skd,
      flightNo: data.flightNo,
      flightPlanId: data.flightPlanId
    });
    setEditState({
      editing: true,
      edited: false,
      hasTemporaryPoint: false,
      data
    });
  }, [editState, setDetailTarget, setEditState, user]);

  const clearDetail = useCallback(() => {
    //detailTargetを初期化
    setDetailTarget({
      customerCode: '',
      skd: '',
      flightNo: '',
      flightPlanId: ''
    });
    setWxDateAdjust(0);

    if (editState.editing) {
      //editStateを初期化
      setEditState({
        editing: false,
        edited: false,
        hasTemporaryPoint: false,
        data: ''
      });
    }

    if (editState.data.creating && isPlanType) {
      clearCrossSection();
    }
  }, [editState, setDetailTarget, setWxDateAdjust, setEditState, isPlanType, clearCrossSection]);

  const startEdit = useCallback((target) => {
    if (editState.editing) {
      // console.log("skip startEdit(already started)");
      return;
    }
    setEditState({
      editing: true,
      edited: false,
      hasTemporaryPoint: false,
      data: {...target}
    });
  }, [editState, setEditState]);

  const isEdited = useCallback(() => {
    if (!editState.editing || !editState.edited) {
      return false;
    }
    //新規作成
    if (editState.data.creating) {
      return true;
    }
    //更新
    else {
      const newData = deepmerge({}, editState.data);
      newData.routeList.forEach(p => {
        if ("temporaryPoints" in p) {
          delete p.temporaryPoints;
        }
      });
      const oldData = fpList.filter(d => isEqualFlightPlan(d, editState.data))[0];

      //更新用パラメータを作成し、変更された項目の有無で判定
      const plan = _makeUpdateData(newData, oldData);
      return Object.keys(plan).length > 3;
    }
  }, [fpList, editState]);

  const endEdit = useCallback(async (save = true) => {
    if (!editState.editing) {
      // console.log("skip endEdit(not started)");
      return;
    }
    if (!save || !editState.edited) {
      setEditState({
        editing: false,
        edited: false,
        hasTemporaryPoint: false,
        data: ''
      });
      return;
    }

    try {
      const newList = [];
      let newData;
      let result;
      //新規作成
      if (editState.data.creating) {
        newData = deepmerge({}, editState.data);
        delete newData.creating;

        //フライトプラン新規登録API呼び出し
        result = await _registerFlightPlan(newData);

        fpList.forEach(d => {
          newList.push(d);
        });
        newList.push(newData);
      }
      //更新
      else {
        let oldData;
        fpList.forEach(d => {
          //編集中のプランの場合
          if (isEqualFlightPlan(d, editState.data)) {
            oldData = d;
            newData = deepmerge({}, editState.data);
            newData.routeList.forEach(p => {
              if ("temporaryPoints" in p) {
                delete p.temporaryPoints;
              }
            });
            newList.push(newData);
          } else {
            newList.push(d);
          }
        });

        const plan = _makeUpdateData(newData, oldData);
        if (Object.keys(plan).length > 3) {
          //フライトプラン更新API呼び出し
          result = await _updateFlightPlan(plan);
        } else {
          setEditState({
            editing: false,
            edited: false,
            hasTemporaryPoint: false,
            data: ''
          });
          return;
        }
      }

      if (result && result.flightplan_id) {
        newData.flightPlanId = result.flightplan_id;
        if (editState.data.creating) {
          newData.flightNo = result.flightplan_id;
        }
        setFpList(newList);
        setEditState({
          editing: false,
          edited: false,
          hasTemporaryPoint: false,
          data: ''
        });
        setDetailTarget({
          ...detailTarget,
          flightPlanId: result.flightplan_id,
          flightNo: editState.data.creating ? result.flightplan_id : detailTarget.flightNo
        });
      }
    } catch (e) {
      if (e.response?.status === 428) {
        //編集モードを解除してデータ取り直し
        setEditState({
          editing: false,
          edited: false,
          hasTemporaryPoint: false,
          data: ''
        });
      }
      throw e;
    }

  }, [fpList, editState, detailTarget, setFpList, setEditState, setDetailTarget]);

  const editFlightPlan = useCallback((target, edited = true) => {
    if (!editState.editing) {
      // console.log("skip editFlightPlan(not started)");
      return;
    }

    if (target.isLinksPlan) {
      const bfDep = editState.data.routeList[0];
      const bfArr = editState.data.routeList[editState.data.routeList.length-1];
      const dep = target.routeList[0];
      const arr = target.routeList[target.routeList.length-1];
      if (bfDep.name !== dep.name) {
        target.depPointName = dep.name;
      }
      if (bfDep.coordinate[0] !== dep.coordinate[0]) {
        target.depPointLon = dep.coordinate[0];
      }
      if (bfDep.coordinate[1] !== dep.coordinate[1]) {
        target.depPointLat = dep.coordinate[1];
      }
      if (bfDep.transitTime !== dep.transitTime) {
        const etd = new WRAP.Core.DateTime(new Date(dep.transitTime));
        target.etd = etd.format('YYYY-MM-DD[T]HH:mm:ss', 'utc');
        //TODO LTは日本時間固定
        target.etdLt = etd.format('YYYY-MM-DD[T]HH:mm:ss', 'Asia/Tokyo');
      }
      if (bfArr.name !== arr.name) {
        target.arrPointName = arr.name;
      }
      if (bfArr.coordinate[0] !== arr.coordinate[0]) {
        target.arrPointLon = arr.coordinate[0];
      }
      if (bfArr.coordinate[1] !== arr.coordinate[1]) {
        target.arrPointLat = arr.coordinate[1];
      }
      if (bfArr.transitTime !== arr.transitTime) {
        const eta = new WRAP.Core.DateTime(new Date(arr.transitTime));
        target.eta = eta.format('YYYY-MM-DD[T]HH:mm:ss', 'utc');
        //TODO LTは日本時間固定
        target.etaLt = eta.format('YYYY-MM-DD[T]HH:mm:ss', 'Asia/Tokyo');
      }
      const props = _makeProps(target);
      target.routeList.forEach(route => {
        route.props = props;
      });
    }

    const _edited = editState.edited || !!edited;
    const hasTemporaryPoint = target.routeList.filter(p => p.temporaryPoints && p.temporaryPoints.length > 0).length > 0;

    setEditState({
      ...editState,
      edited: _edited,
      hasTemporaryPoint: hasTemporaryPoint,
      data: target
    });

    if (isPlanType) {
      setCsTypePlan(target.aircraftCode, target.etd);
      setCsTarget({
        customerCode: target.customerCode,
        skd: target.skd,
        flightNo: target.flightNo,
        flightPlanId: target.flightPlanId
      });
    }

  }, [editState, setEditState, isPlanType, setCsTypePlan, setCsTarget]);

  const setCrossSection = useCallback((target) => {
    if (target !== '') {
      let fp;
      let fpIndex;
      if (editState.data !== '' && isEqualFlightPlan(editState.data, target)) {
        fp = editState.data;
      } else {
        fpIndex = searchFlightPlanIndex(fpList, target);
        if (fpIndex !== -1) {
          fp = fpList[fpIndex];
        }
      }

      if (fp) {
        //非表示の場合は表示する
        if (fpIndex !== undefined && !fp.visible) {
          const newList = [...fpList];
          newList.splice(fpIndex, 1, {
            ...fp,
            visible: true
          });
          setFpList(newList);
        }
        setCsTypePlan(fp.aircraftCode, fp.etd);
        setCsTarget({
          customerCode: fp.customerCode,
          skd: fp.skd,
          flightNo: fp.flightNo,
          flightPlanId: fp.flightPlanId
        });

        if (isMapOnly) {
          changeHorizontal();
        }
      }
    } else {
      clearCrossSection();
    }

  }, [fpList, setFpList, editState, setCsTarget, setCsTypePlan, isMapOnly, changeHorizontal, clearCrossSection]);

  const setHighlightPlan = useCallback((highlightPlan) => {
    setHighlightFlightPlan(deepmerge({}, highlightPlan));
  }, [setHighlightFlightPlan]);

  const clearHighlightPlan = useCallback(() => {
    setHighlightFlightPlan({
      source: '',
      plan: {},
    });
  }, [setHighlightFlightPlan]);

  const disableFlightPlan = useCallback(async (target) => {

    const newList = fpList.filter(d => !isEqualFlightPlan(d, target));
    if (newList.length === fpList.length) {
      // console.log("skip disableFlightPlan(not exists)");
      return true;
    }

    try {
      //計画経路非表示化API呼び出し
      await _disableFlightPlan(target);
  
      setFpList(newList);

      if (isEqualFlightPlan(detailTarget, target)) {
        setDetailTarget({
          customerCode: '',
          skd: '',
          flightNo: '',
          flightPlanId: ''
        });
        setWxDateAdjust(0);

        setEditState({
          editing: false,
          edited: false,
          hasTemporaryPoint: false,
          data: ''
        });
      }

      if (isEqualFlightPlan(target, csTarget)) {
        clearCrossSection();
      }

      if (isEqualFlightPlan(target, highlightPlan)) {
        clearHighlightPlan();
      }

    } catch (e) {
      if (e.response?.status === 428) {
        //フライトプラン再取得を行うためフラグをセット
        setFlightPlanRefresh(true);
      }
      throw e;
    }
 
  }, [fpList, detailTarget, csTarget, highlightPlan, setFpList, setDetailTarget, setWxDateAdjust, setEditState, clearCrossSection, clearHighlightPlan, setFlightPlanRefresh]);

  const visibilityChangeFlightPlan = useCallback(async (target, visible) => {
    const newList = [];
    fpList.forEach(d => {
      if (isEqualFlightPlan(d, target)) {
        newList.push({
          ...d,
          visible: visible
        });
      } else {
        newList.push(d);
      }
    });
    setFpList(newList);

    //詳細画面表示中のプランを非表示にした場合、detailTargetを初期化
    if (!visible && isEqualFlightPlan(target, detailTarget)) {
      setDetailTarget({
        customerCode: '',
        skd: '',
        flightNo: '',
        flightPlanId: ''
      });
      setWxDateAdjust(0);
    }

    //断面図表示中のプランを非表示にした場合、
    if (!visible && isEqualFlightPlan(target, csTarget)) {
      clearCrossSection();
    }

  }, [fpList, setFpList, detailTarget, setDetailTarget, setWxDateAdjust, csTarget, clearCrossSection]);

  //FlightPlanが更新された時に各種フラグを更新する
  const planUpdated = useCallback(() => {
    if (!planInitialized) {
      setFlightPlanInitialized(true);
    }
    if (needsRefreshPlan) {
      setFlightPlanRefresh(false);
    }
  }, [planInitialized, setFlightPlanInitialized, needsRefreshPlan, setFlightPlanRefresh]);

  const setDragging = useCallback((x, y, index, temporaryIdx=undefined) => {
    const point = {
      pos: {x, y},
      data: {index}
    };
    if (temporaryIdx !== undefined) {
      point.data.temporary = true;
      point.data.temporaryIdx = temporaryIdx;
    }
    setDraggingPoint(point);
  }, [setDraggingPoint]);

  const clearDragging = useCallback(() => {
    setDraggingPoint(null);
  }, [setDraggingPoint]);

  return {
    flightPlanList: fpList,
    editState,
    detail,
    cs,
    getFlightPlan,
    setFlightPlanList,
    createFlightPlanStart,
    setDetail,
    clearDetail,
    startEdit,
    editFlightPlan,
    isEdited,
    endEdit,
    disableFlightPlan,
    visibilityChangeFlightPlan,
    setCrossSection,
    clearCrossSection,
    highlightPlan,
    setHighlightPlan,
    clearHighlightPlan,
    // modelType,
    // setModelType,
    wxDateAdjust,
    setWxDateAdjust,
    // modelPointData,
    planInitialized,
    planUpdated,
    needsRefreshPlan,
    draggingPoint,
    setDragging,
    clearDragging,
    flightPlanLoadError,
    setFlightPlanLoadError
  };
};

const useFlightPlanDetail = () => {
  const setDetail = useRecoilCallback(({snapshot, set}) => async(target, asEditMode = false) => {
    const editState = await snapshot.getPromise(flightPlanEditState);
    if (asEditMode && editState.editing) {
      return;
    }
    const fpList = await snapshot.getPromise(flightPlanListState);
    const fpIndex = searchFlightPlanIndex(fpList, target);
    if (fpIndex !== -1) {
      const fp = fpList[fpIndex];

      const route = fp.routeList.flatMap(p=>p.type==="airway" ? p.fixes.slice(1, p.fixes.length -1) : p);
      const perspective = getRoutePerspective(route.map(p => p.coordinate))
      set(mapPerspectiveState, perspective);

      //非表示の場合は表示する
      if (!fp.visible) {
        const newList = [...fpList];
        newList.splice(fpIndex, 1, {
          ...fp,
          visible: true
        });
        set(flightPlanListState, newList);
      }

      set(flightPlanDetailTargetState, {
        customerCode: target.customerCode,
        skd: target.skd,
        flightNo: target.flightNo,
        flightPlanId: target.flightPlanId
      });


      if (asEditMode) {
        set(flightPlanEditState, {
          editing: asEditMode,
          edited: false,
          hasTemporaryPoint: false,
          data: {...fp}
        });
      }
    }
  });

  return {
    setDetail
  }
};

const useErrorMessage = () => {
  const removeDuplicateMessages = useSetRecoilState(removeDuplicateMessagesSelector);
  const {showError, removeAlert} = useAlert();

  const showErrorWithMessage = useCallback(message => {
    const errorMessage = makeErrorMessage(message);
    removeDuplicateMessages(errorMessage);
    return showError(errorMessage);
  }, [showError, removeDuplicateMessages]);

  const clearErrorMessage = useCallback(messageInfo => {
    removeAlert(messageInfo);
  }, [removeAlert]);

  return {
    showErrorWithMessage,
    clearErrorMessage
  };
};

export const useFlightPlanDetailData = () => {
  const fpList = useRecoilValue(flightPlanListState);
  const detailTarget = useRecoilValue(flightPlanDetailTargetState);
  const editState = useRecoilValue(flightPlanEditState);

  const getFlightPlan = useCallback((target) => {
    if (editState.editing && isEqualFlightPlan(editState.data, target)) {
      return editState.data;
    }
    return searchFlightPlan(fpList, target) || '';
  }, [editState, fpList]);

  return useMemo(() => {
    return getFlightPlan(detailTarget);
  }, [detailTarget, getFlightPlan]);
};

export const useFlightPlanModel = () => {
  const modelType = useRecoilValue(selectedModelTypeState);
  const [modelLoading, setModelLoading] = useRecoilState(modelLoadingState);
  const [modelPointData, setModelPointData] = useRecoilState(modelPointDataState);
  const modelValidtime = useRecoilValue(modelValidtimeState);
  const {showErrorWithMessage, clearErrorMessage} = useErrorMessage();
  const errorMessageInfo = useRef({});
  const wxDateAdjust = useRecoilValue(wxDateAdjustmentState);

  // modelTypeとmodelValidtimeを同時に同一トランザクションで更新する
  const txModelTypeValidTime = useRecoilTransaction_UNSTABLE((transaction) => (modelType, validTime) => {
    // 同一トランザクションで更新しないとmodelTypeを更新した時点でFlightPlanDetailObserverのuseEffectが発火してしまう
    transaction.set(selectedModelTypeState, modelType);
    transaction.set(modelValidtimeState, validTime);
    transaction.set(modelPointDataState, []);
  }, []);

  const showModel = useCallback((modelType) => {
    if (!modelType) {
      txModelTypeValidTime(null, null);
      return;
    }

    //ローディング開始。一連の流れでmodelDataLoadが完了する間はtrue
    setModelLoading(true);

    const promises = [];
    if (modelType === 'msm') {
      const groundPromise = new Promise((resolve, reject) => {
        models[msmGround].load('validtime', {data: msmGround}, true, (data, urls, option) => {
          if (!isEmpty(data)) {
            if (errorMessageInfo.current.msmGround) {
              clearErrorMessage(errorMessageInfo.current.msmGround);
              errorMessageInfo.current.msmGround = null;
            }
            resolve(
              {
                valid_times: data.valid_times.map(t => t.split('+')[0]),
                basetime: data.basetime,
                low: true,
                ...option
              }
            );
          }
        }, () => {
          errorMessageInfo.current.msmGround = showErrorWithMessage({
            message: `${msmGround} validtime`,
            additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
          });
          reject();
        });
      });
      promises.push(groundPromise);
      const upperPromise = new Promise((resolve, reject) => {
        models[msmUpper].load('validtime', {data: msmUpper}, true, (data, urls, option) => {
          if (!isEmpty(data)) {
            if (errorMessageInfo.current.msmUpper) {
              clearErrorMessage(errorMessageInfo.current.msmUpper);
              errorMessageInfo.current.msmUpper = null;
            }
            resolve(
              {
                valid_times: data.valid_times.map(t => t.split('+')[0]),
                basetime: data.basetime,
                hi: true,
                ...option
              }
            );
          }
        }, () => {
          errorMessageInfo.current.msmUpper = showErrorWithMessage({
            message: `${msmUpper} validtime`,
            additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
          });
          reject();
        });
      });
      promises.push(upperPromise);
    } else if (modelType === 'lfm') {
      const groundPromise = new Promise((resolve, reject) => {
        models[lfmGround].load('validtime', {data: lfmGround}, true, (data, urls, option) => {
          if (!isEmpty(data)) {
            if (errorMessageInfo.current.lfmGround) {
              clearErrorMessage(errorMessageInfo.current.lfmGround);
              errorMessageInfo.current.lfmGround = null;
            }
            resolve(
              {
                valid_times: data.valid_times.map(t => t.split('+')[0]),
                basetime: data.basetime,
                low: true,
                ...option
              }
            );
          }
        }, () => {
          errorMessageInfo.current.lfmGround = showErrorWithMessage({
            message: `${lfmGround} validtime`,
            additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
          });
          reject();
        });
      });
      promises.push(groundPromise);
      const upperPromise = new Promise((resolve, reject) => {
        models[lfmUpper].load('validtime', {data: lfmUpper}, true, (data, urls, option) => {
          if (!isEmpty(data)) {
            if (errorMessageInfo.current.lfmUpper) {
              clearErrorMessage(errorMessageInfo.current.lfmUpper);
              errorMessageInfo.current.lfmUpper = null;
            }
            resolve(
              {
                valid_times: data.valid_times.map(t => t.split('+')[0]),
                basetime: data.basetime,
                hi: true,
                ...option
              }
            );
          }
        }, () => {
          errorMessageInfo.current.lfmUpper = showErrorWithMessage({
            message: `${lfmUpper} validtime`,
            additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
          });
          reject();
        });
      });
      promises.push(upperPromise);
    } else if (modelType === 'gsm') {
      const groundPromise = new Promise((resolve, reject) => {
        models[gsmGround].load('validtime', {data: gsmGround}, true, (data, urls, option) => {
          if (!isEmpty(data)) {
            if (errorMessageInfo.current.gsmGround) {
              clearErrorMessage(errorMessageInfo.current.gsmGround);
              errorMessageInfo.current.gsmGround = null;
            }
            resolve(
              {
                valid_times: data.valid_times.map(t => t.split('+')[0]),
                basetime: data.basetime,
                low: true,
                ...option
              }
            );
          }
        }, () => {
          errorMessageInfo.current.gsmGround = showErrorWithMessage({
            message: `${gsmGround} validtime`,
            additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
          });
          reject();
        });
      });
      promises.push(groundPromise);
      const upperPromise = new Promise((resolve, reject) => {
        models[gsmUpper].load('validtime', {data: gsmUpper}, true, (data, urls, option) => {
          if (!isEmpty(data)) {
            if (errorMessageInfo.current.gsmUpper) {
              clearErrorMessage(errorMessageInfo.current.gsmUpper);
              errorMessageInfo.current.gsmUpper = null;
            }
            resolve(
              {
                valid_times: data.valid_times.map(t => t.split('+')[0]),
                basetime: data.basetime,
                hi: true,
                ...option
              }
            );
          }
        }, () => {
          errorMessageInfo.current.gsmUpper = showErrorWithMessage({
            message: `${gsmUpper} validtime`,
            additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
          });
          reject();
        });
      });
      promises.push(upperPromise);
    } else {
      const groundPromise = new Promise((resolve, reject) => {
        models[gfs].load('validtime', {data: gfs}, true, (data, urls, option) => {
          if (!isEmpty(data)) {
            if (errorMessageInfo.current.gfs) {
              clearErrorMessage(errorMessageInfo.current.gfs);
              errorMessageInfo.current.gfs = null;
            }
            resolve(
              {
                valid_times: data.valid_times.map(t => t.split('+')[0]),
                basetime: data.basetime,
                // gfsはhi, low共通
                hi: true,
                low: true,
                ...option
              }
            );
          }
        }, () => {
          errorMessageInfo.current.gfs = showErrorWithMessage({
            message: `${gfs} validtime`,
            additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
          });
          reject();
        });
      });
      promises.push(groundPromise);
    }
    Promise.allSettled(promises).then(values => {
      const vt = values.map(v => v.value ? v.value : {});
      txModelTypeValidTime(modelType, vt);
      // setModelValidtime(vt);
    })
  }, [txModelTypeValidTime, showErrorWithMessage, clearErrorMessage, setModelLoading]);

  const modelDataLoad = useCallback((flightPlan) => {
    if (!flightPlan.routeList || !modelValidtime) {
      setModelPointData([]);
      setModelLoading(false);
      return;
    }
    setModelLoading(true);
    const _routeList = flightPlan.routeList.flatMap(p=>p.type==="airway" ? p.fixes.slice(1, p.fixes.length -1) : p);
    const promises = _routeList.map(point => {

      const promises = [];
      if (point.coordinate.filter(v => v !== null).length !== 2 || !point.transitTime) {
        promises.push(new Promise(resolve => {
          resolve({});
        }));
      } else {
        //高度が0より大きくてもLOWCLD、MIDCLD、UPRCLDを取得するためにGroundのデータは取得する
        promises.push(new Promise((resolve, reject) => {
          const validData = modelValidtime.find(v => v.low);
          if (validData) {
            const dh = models[validData.data];
            const option = _makeModelApiOption(validData, point, wxDateAdjust, 0);
            if (option) {
              dh.load('data', option, false, data => {
                resolve(data);
              }, () => {
                showErrorWithMessage({
                  message: `${validData.data} data`,
                  additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
                });
                reject();
              });
            } else {
              resolve({past: true});
            }
          } else {
            resolve({past: true});
          }
        }));

        if (point.alt !== 0) {
          promises.push(new Promise((resolve, reject) => {
            const validData = modelValidtime.find(v => v.hi);
            if (validData) {
              const dh = models[validData.data];
              const option = _makeModelApiOption(validData, point, wxDateAdjust);
              if (option) {
                dh.load('data', option, false, data => {
                  resolve(data);
                }, () => {
                  showErrorWithMessage({
                    message: `${validData.data} data`,
                    additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
                  });
                  reject();
                });
              } else {
                resolve({past: true});
              }
            } else {
              resolve({past: true});
            }
          }));
        }
      }
      return promises;
    }).flat();

    Promise.allSettled(promises).then(values => {
      const formatted = values.map(v => v.value ? v.value : {});
      const hasData = formatted.reduce((prev, current) => prev || Object.keys(current).length > 0, false);
      if (hasData) {
        const pointData = [];
        let idx = 0;
        _routeList.forEach((route) => {
          if (!route.transitTime) {
            pointData.push(formatted[idx++]);
          } else if (route.alt === 0) {
            pointData.push(formatted[idx++]);
          } else {
            const lowValue = formatted[idx++];
            const highValue = formatted[idx++];
            //LOWCLD、MIDCLD、UPRCLDのみ、Groundのデータを使用する
            pointData.push({
              ...highValue,
              LOWCLD: lowValue.LOWCLD,
              MIDCLD: lowValue.MIDCLD,
              UPRCLD: lowValue.UPRCLD
            });
          }
          if (route.temporaryPoints) {
            route.temporaryPoints.forEach(() => {
              pointData.push({});
            })
          }
        })
        setModelPointData(pointData);
      }
      setModelLoading(false);
    })

  }, [modelValidtime, setModelPointData, wxDateAdjust, showErrorWithMessage, setModelLoading]);

  return {
    modelType,
    setModelType: showModel,
    modelDataLoad,
    modelPointData,
    modelLoading,
  }
};

export const useFlightPlanGassScale = () => {
  const [gassScaleVisible, setGassScaleVisible] = useRecoilState(gassScaleVisibleState);
  const [gassDataLoading, setGassDataLoading] = useRecoilState(gassDataLoadingState);
  const [gassPointData, setGassPointData] = useRecoilState(gassPointDataState);
  const {dhGassScaleHvis, dhGassScaleCling, dhElevation} = useWrapDh();
  const wxDateAdjust = useRecoilValue(wxDateAdjustmentState);
  const {showErrorWithMessage} = useErrorMessage();

  //GASS Scaleのvalidtimeを取得後(retained)、GASSの表示ステート切り替え
  const showGassScale = useCallback(() => {

    //ローディング開始。一連の流れでgassScaleDataLoadが完了する間はtrue
    setGassDataLoading(true);

    const promises = [];
    //GassScale(HVIS）のvalidtime取得
    promises.push(new Promise(resolve => {
      dhGassScaleHvis.load('validtime', null, true, (data) => {
        resolve(data?.validtime);
      });
    }));

    //GassScale(CLING）のvalidtime取得
    promises.push(new Promise(resolve => {
      dhGassScaleCling.load('validtime', null, true, (data) => {
        resolve(data?.validtime);
      });
    }));

    Promise.allSettled(promises).then(values => {
      if (values.some(v => v.status === 'fulfilled')) {
        setGassScaleVisible(true);
      }
    });

  }, [setGassScaleVisible, dhGassScaleHvis, dhGassScaleCling, setGassDataLoading]);

  const hideGassScale = useCallback(() => {
    setGassScaleVisible(false);
    setGassPointData({});
    setGassDataLoading(false)
  }, [setGassScaleVisible, setGassPointData, setGassDataLoading]);

  const gassScaleDataLoad = useCallback((flightPlan) => {
    if (!gassScaleVisible) {
      return;
    }
    const nearValid = (validData, transitTime) => {
      if (!validData || validData.length === 0) return null;
      const validTimes = [...validData];
      validTimes.sort();
      const firstValid = parseUTCDate(validTimes[0]).getTime();
      const lastValid = parseUTCDate(validTimes[validTimes.length - 1]).getTime();
      let diff = 0;
      if (validTimes.length > 1) {
        const nextValid = parseUTCDate(validTimes[1]).getTime();
        diff = nextValid - firstValid;
      }
      const firstThreshold = firstValid - diff / 2;
      const lastThreshold = lastValid + diff / 2;
      if (firstThreshold > transitTime || lastThreshold < transitTime) {
        return null;
      }
      return nearValidTime(validData, transitTime)
    };

    const load = async () => {
      if (!flightPlan.routeList) {
        return;
      }

      setGassDataLoading(true);

      const _routeList = flightPlan.routeList.flatMap(p => p.type === "airway" ? p.fixes.slice(1, p.fixes.length - 1) : p);
      const newPoint = {};
      for await (const point of _routeList) {
        if (point.coordinate.filter(v => v !== null).length === 2 && point.transitTime) {
          const transitTime = point.transitTime + wxDateAdjust * 86400 * 1000;
          const key = `${point.coordinate}:${transitTime}`;
          if (gassPointData[key]) {
            continue;
          }
          const promises = [];
          const validDataHvis = dhGassScaleHvis.retained.validtime?.validtime;
          const nearValidTimeHvis = nearValid(validDataHvis, transitTime);
          if (nearValidTimeHvis) {
            //GassScale(HVIS）のpointValue
            promises.push(new Promise((resolve, reject) => {
              dhGassScaleHvis.getPointValue('data', point.coordinate, {validtime: nearValidTimeHvis}, null, (value) => {
                resolve({hvis: value});
              }, () => {
                showErrorWithMessage({
                  message: COMMUNICATE_ERROR_MESSAGE_GASS_SCALE_HVIS,
                  additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
                });
                reject({hvis: undefined});
              });
            }));
          }
          const validDataCling = dhGassScaleCling.retained.validtime?.validtime;
          const nearValidTimeCling = nearValid(validDataCling, transitTime);
          if (nearValidTimeCling) {
            //GassScale(CLING）のpointValue
            promises.push(new Promise((resolve, reject) => {
              dhGassScaleCling.getPointValue('data', point.coordinate, {validtime: nearValidTimeCling}, null, (value) => {
                resolve({cling: value});
              }, () => {
                showErrorWithMessage({
                  message: COMMUNICATE_ERROR_MESSAGE_GASS_SCALE_CLING,
                  additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
                });
                reject({cling: undefined});
              });
            }));
            //Elevation(標高)のpointValue
            promises.push(new Promise((resolve, reject) => {
              dhElevation.getPointValue('data', point.coordinate, null, null, (value) => {
                resolve({elevation: value});
              }, () => {
                showErrorWithMessage({
                  message: COMMUNICATE_ERROR_MESSAGE_ELEVATION_DATA,
                  additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
                });
                reject({elevation: undefined});
              });
            }));
          }
          const pointData = await Promise.allSettled(promises).then(values => {
            let hvis;
            let cling;
            let elevation;
            values.map(v => v.value ? v.value : v.reason).forEach(d => Object.keys(d).forEach(k => {
              if (k === 'hvis') hvis = d[k];
              if (k === 'cling') cling = d[k];
              if (k === 'elevation') elevation = d[k];
            }));
            return {
              key: `${point.coordinate}:${transitTime}`,
              hvis,
              cling,
              elevation
            }
          });
          newPoint[pointData.key] = pointData;
        }
      }
      if (Object.keys(newPoint).length) {
        setGassPointData({...gassPointData, ...newPoint});
      }
      setGassDataLoading(false);
    }
    load();
  }, [gassScaleVisible, gassPointData, setGassPointData, dhGassScaleHvis, dhGassScaleCling, dhElevation, wxDateAdjust, showErrorWithMessage, setGassDataLoading]);

  return {
    gassScaleVisible,
    showGassScale,
    hideGassScale,
    gassScaleDataLoad,
    gassPointData,
    gassDataLoading,
  }
};

export const useFlightPlanGeography = () => {
  const [geographyVisible, setGeographyVisible] = useRecoilState(geographyVisibleState);
  const [geographyDataLoading, setGeographyDataLoading] = useRecoilState(geographyDataLoadingState);
  const [geographyPointData, setGeographyPointData] = useRecoilState(geographyPointDataState);
  const wxDateAdjust = useRecoilValue(wxDateAdjustmentState);
  const timeZone = useRecoilValue(timeZoneSelector);
  const {showErrorWithMessage} = useErrorMessage();
  const {dhElevation, dhCalendar} = useWrapDh();

  const showGeography = useCallback(()=> {
    setGeographyVisible(true);
  }, [setGeographyVisible]);

  //Geographyの表示ステートをfalseと地点毎のデータを破棄
  const hideGeography = useCallback(() => {
    setGeographyVisible(false);
    setGeographyPointData({});
  }, [setGeographyVisible, setGeographyPointData]);

  const getPointTargetDate = useCallback((flightPlan, index) => {
    let sunriseTargetTime;
    let isSkdSelected = false;
    const _routeList = flightPlan.routeList.flatMap(p => p.type === "airway" ? p.fixes.slice(1, p.fixes.length - 1) : p);
    const point = _routeList[index];
    if (!point) {
      return;
    }
    if (point.transitTime) {
      sunriseTargetTime = point.transitTime;
    } else {
      // transitTimeが設定されていなければ前のpointのtransitTimeを参照。
      for (let i = index - 1; i >= 0; i--) {
        if (_routeList[i].transitTime) {
          sunriseTargetTime = _routeList[i].transitTime;
          break;
        }
      }
      if (!sunriseTargetTime) {
        // 一つもtransitTimeが設定されていない場合は、SKD(日本時間)
        sunriseTargetTime = (new Date(`${flightPlan.skd} 00:00:00+09:00`)).getTime();
        isSkdSelected = true;
      }
    }

    const sunriseTargetDate = new Date(sunriseTargetTime + wxDateAdjust * 86400 * 1000);
    return !isSkdSelected ? formatTimeZoneDate(sunriseTargetDate, timeZone, 'YYYYMMDD'): formatTimeZoneDate(sunriseTargetDate, 'Asia/Tokyo', 'YYYYMMDD');
  }, [wxDateAdjust, timeZone]);

  const geographyDataLoad = useCallback((flightPlan) => {
    if (!flightPlan || !geographyVisible) {
      return;
    }
    const load = async () => {
      setGeographyDataLoading(true);
      const _routeList = flightPlan.routeList.flatMap(p => p.type === "airway" ? p.fixes.slice(1, p.fixes.length - 1) : p);
      const calendarDateMap = new Map();
      const promises = [];
      const elevationRequested = {};
      const calendarRequested = {};

      const loadElevationPointData = (coordinate) => {
        return new Promise((resolve, reject) => {
          dhElevation.getPointValue('data', coordinate, null, null, (value) => {
            resolve(value);
          }, () => {
            reject();
          });
        });
      }

      for (const [index, point] of _routeList.entries()) {

        if (point.coordinate.filter(v => v !== null).length === 2) {
          const calendarDate = getPointTargetDate(flightPlan, index);
          const key = `${point.coordinate}:${calendarDate}`;

          if (geographyPointData[key]?.elevation === undefined && !elevationRequested[key]) {
            elevationRequested[key] = true;
            // Elevationを取得（getPointValueが非同期に同一タイルにリクエストを投げることができない（後発リクエストが捨てられる）ので同期で取得する）
            await loadElevationPointData(point.coordinate).then((elevation)=> {
              promises.push(Promise.resolve({key, elevation}));
            }).catch(()=> {
              showErrorWithMessage({
                message: COMMUNICATE_ERROR_MESSAGE_ELEVATION_DATA,
                additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
              });
            });
          }

          if (geographyPointData[key]?.sunrise === undefined && !calendarRequested[key]) {
            calendarRequested[key] = true;
            const calendarPoint = {
              NAME: key,
              LOND: point.coordinate[0],
              LATD: point.coordinate[1],
              ALT: 0
            };
            if (calendarDateMap.has(calendarDate)) {
              calendarDateMap.get(calendarDate).push(calendarPoint);
            } else {
              calendarDateMap.set(calendarDate, [calendarPoint]);
            }
          }
        }
      }

      if (calendarDateMap.size > 0) {
        calendarDateMap.forEach((points, validTime) => {
          promises.push(new Promise((resolve, reject) => {
            dhCalendar.load('data', {
              valid_time: validTime,
              points: JSON.stringify(points)
            }, false, (data) => {
              resolve(data);
            }, () => {
              showErrorWithMessage({
                message: COMMUNICATE_ERROR_MESSAGE_SUNRISE_DATA,
                additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER,
              });
              reject();
            });
          }));
        });
      }
      const newPoint = {};
      await Promise.allSettled(promises).then(values => {
        values.forEach(v => {
          if (v.status === 'fulfilled') {
            if (v.value.elevation !== undefined) {
              // elevation
              newPoint[v.value.key] = {...newPoint[v.value.key], ...{elevation: v.value.elevation}};
            } else {
              // sunrise, sunset
              v.value.data.forEach(p => {
                newPoint[p.point.NAME] = {...newPoint[p.point.NAME], ...{aqc:p.AQC, sunrise: p.rise_time, sunset: p.set_time}};
              });
            }
          }
        });
      });
      if (Object.keys(newPoint).length) {
        setGeographyPointData({...geographyPointData, ...newPoint});
      }
      setGeographyDataLoading(false);
    };
    load();
  }, [geographyVisible, geographyPointData, getPointTargetDate, dhCalendar, dhElevation, setGeographyPointData, showErrorWithMessage, setGeographyDataLoading]);

  return {
    geographyVisible,
    showGeography,
    hideGeography,
    getPointTargetDate,
    geographyDataLoad,
    geographyPointData,
    geographyDataLoading,
  };
};

export const useFlightPlanDistance = () => {
  const [distanceVisible, setDistanceVisible] = useRecoilState(distanceVisibleState);

  const showDistance = useCallback(()=> {
    setDistanceVisible(true);
  }, [setDistanceVisible]);

  //Distanceの表示ステートをfalse
  const hideDistance = useCallback(() => {
    setDistanceVisible(false);
  }, [setDistanceVisible]);

  return {
    distanceVisible,
    showDistance,
    hideDistance
  };
};

//再描画抑制用にCopilotList用にuseFlightPlanから切り出し
export const useFlightPlanForCopilotList = () => {
  const flightPlanList = useRecoilValue(flightPlanListState);
  const editState = useRecoilValue(flightPlanEditState);
  const {setDetail} = useFlightPlanDetail();
  return {
    flightPlanList,
    setDetail,
    editState,
  }
};

//対象フライトプラン検索(インデックス番号を返却)
const searchFlightPlanIndex = (list, target) => {
  return list.findIndex(d => isEqualFlightPlan(d, target));
};

//対象フライトプラン検索
export const searchFlightPlan = (list, target) => {
  const filtered = list.filter(d => isEqualFlightPlan(d, target));
  return filtered.length > 0 ? filtered[0] : null;
};

//フライトプラン比較
export const isEqualFlightPlan = (a, b) => {
  if (!a || !b) return false;
  if (a.flightPlanId && b.flightPlanId && a.flightPlanId === b.flightPlanId) return true;
  return a.customerCode === b.customerCode && a.flightNo === b.flightNo;
};

const _formatWaypoint = (flightPlan) => {

  const makeTransitTime = (transitTime) => {
    if (!transitTime) {
      return null;
    }
    const d = new Date();
    d.setTime(transitTime);
    return formatTimeZoneDate(d, 'utc', 'YYYY-MM-DDTHH:mm:ss[Z]');
  };

  const plan = {
    scheduled_route: "",
    waypoint: []
  };
  let seq = 0;
  flightPlan.routeList.forEach((p, i, list) => {
    if (i > 0 && i < list.length - 1) {
      const prev = list[i-1];
      const next = list[i+1];
      if (p.type !== 'airway') {
        if (prev.type !== 'airway' && next.type !== 'airway') {
          const wp = {
            seq: seq,
            type: p.type,
            name: p.name,
            airway_name: null,
            lon: p.coordinate[0],
            lat: p.coordinate[1],
            altitude_ft: p.alt,
            time: makeTransitTime(p.transitTime),
            min_altitude_ft: null,
            max_altitude_ft: null,
          };
          plan.waypoint.push(wp);
          plan.scheduled_route += (plan.scheduled_route === "" ? "" : " ") + wp.name;
          seq++;
        } else if (prev.type === 'airway' && next.type === 'airway') {
          const wp = {
            seq: seq,
            type: p.type,
            name: p.name,
            airway_name: null,
            lon: p.coordinate[0],
            lat: p.coordinate[1],
            altitude_ft: p.alt,
            time: makeTransitTime(p.transitTime),
            min_altitude_ft: null,
            max_altitude_ft: null,
          };
          plan.waypoint.push(wp);
          seq++;
        }
      } else {
        const airwayName = p.name;
        let firstPointName;
        let lastPointName;
        p.fixes.forEach((ap, j, airwayList) => {
          if (j === 0) {
            firstPointName = ap.name;
          }
          if (j === airwayList.length - 1) {
            lastPointName = ap.name;
          }
          const wp = {
            seq: seq,
            type: ap.type,
            name: ap.name,
            airway_name: airwayName,
            lon: ap.coordinate[0],
            lat: ap.coordinate[1],
            altitude_ft: ap.alt,
            time: makeTransitTime(ap.transitTime),
            min_altitude_ft: ap.minAlt,
            max_altitude_ft: ap.maxAlt,
          };
          plan.waypoint.push(wp);
          seq++;
        });
        if (plan.scheduled_route.endsWith(firstPointName)) {
          plan.scheduled_route += (plan.scheduled_route === "" ? "" : " ") + `${airwayName} ${lastPointName}`;
        } else {
          plan.scheduled_route += (plan.scheduled_route === "" ? "" : " ") + `${firstPointName} ${airwayName} ${lastPointName}`;
        }
      }
    }
  });

  return plan;
}

//フライトプラン新規登録API実行
const _registerFlightPlan = async (flightPlan) => {

  const apiUrl = process.env.REACT_APP_FLIGHTPLAN_API_URL;
  const apiKey = process.env.REACT_APP_FLIGHTPLAN_API_KEY;

  const dep = flightPlan.routeList[0];
  const arr = flightPlan.routeList[flightPlan.routeList.length - 1];
  const plan = {
    customer_code: flightPlan.customerCode,
    aircraft_code: flightPlan.aircraftCode ? flightPlan.aircraftCode : null,
    flight_type: flightPlan.flightType ? flightPlan.flightType : null,
    skd: flightPlan.skd.replaceAll('/','-'),
    etd_utc: flightPlan.etd,
    eta_utc: flightPlan.eta,
    etd_lt: flightPlan.etdLt,
    eta_lt: flightPlan.etaLt,
    dep_point_name: flightPlan.depPointName,
    dep_point_lat: flightPlan.depPointLat,
    dep_point_lon: flightPlan.depPointLon,
    dep_point_alt_ft: dep && dep.alt !== null && dep.alt !== undefined ? dep.alt : 0,
    arr_point_name: flightPlan.arrPointName,
    arr_point_lat: flightPlan.arrPointLat,
    arr_point_lon: flightPlan.arrPointLon,
    arr_point_alt_ft: arr && arr.alt !== null && arr.alt !== undefined ? arr.alt : 0,
  };

  const wp = _formatWaypoint(flightPlan);
  plan.scheduled_route = wp.scheduled_route;
  plan.waypoint = wp.waypoint;

  const config = {
    method: 'post',
    url: `${apiUrl}/flightplan/register_flight_plan`,
    headers: {
      'x-api-key': apiKey
    },
    data: plan,
  };

  const data = axios(config)
    .then(response => {
      // console.log(JSON.stringify(response.data));
      return response.data;
    })
    .catch(e => {
      console.log(`Failed to register_flight_plan(${flightPlan.customerCode}:${flightPlan.depPointName}:${flightPlan.arrPointName}).`, e);
      throw e;
    });

  return data;
};

const _makeUpdateData = (flightPlan, beforePlan) => {

  const trimming = (data) => {
    const _data = {...data};
    delete _data.props;
    delete _data.refs;
    delete _data.fixindex;
    return _data;
  };

  const plan = {
    flightplan_id: flightPlan.flightPlanId,
    flight_no: flightPlan.flightNo,
    customer_code: flightPlan.customerCode,
  };

  //機体番号の変更チェック
  if (flightPlan.aircraftCode !== beforePlan.aircraftCode) {
    plan.aircraft_code = flightPlan.aircraftCode ? flightPlan.aircraftCode : null;
  }

  //飛行種別の変更チェック
  if (flightPlan.flightType !== beforePlan.flightType) {
    plan.flight_type = flightPlan.flightType ? flightPlan.flightType : null;
  }

  //DEPの変更チェック
  const dep = trimming(flightPlan.routeList[0]);
  const beforeDep = trimming(beforePlan.routeList[0]);
  if (!isEqualObject(dep, beforeDep)) {
    plan.etd_utc = flightPlan.etd;
    plan.etd_lt = flightPlan.etdLt;
    plan.dep_point_name = flightPlan.depPointName;
    plan.dep_point_lat = flightPlan.depPointLat;
    plan.dep_point_lon = flightPlan.depPointLon;
    plan.dep_point_alt_ft = dep && dep.alt !== null && dep.alt !== undefined ? dep.alt : 0;
  }

  //ARRの変更チェック
  const arr = trimming(flightPlan.routeList[flightPlan.routeList.length - 1]);
  const beforeArr = trimming(beforePlan.routeList[beforePlan.routeList.length - 1]);
  if (!isEqualObject(arr, beforeArr)) {
    plan.arr_point_name = flightPlan.arrPointName;
    plan.arr_point_lat = flightPlan.arrPointLat;
    plan.arr_point_lon = flightPlan.arrPointLon;
    plan.arr_point_alt_ft = arr && arr.alt !== null && arr.alt !== undefined ? arr.alt : 0;
    plan.eta_utc = flightPlan.eta;
    plan.eta_lt = flightPlan.etaLt;
  }

  //経路の変更チェック
  const route = flightPlan.routeList.slice(1, flightPlan.routeList.length - 1).map(trimming);
  const beforeRoute = beforePlan.routeList.slice(1, beforePlan.routeList.length - 1).map(trimming);
  if (!isEqualObject(route, beforeRoute)) {
    const wp = _formatWaypoint(flightPlan);
    plan.scheduled_route = wp.scheduled_route;
    plan.waypoint = wp.waypoint;
  }

  return plan;
};

//フライトプラン更新API実行
const _updateFlightPlan = async (plan) => {

  const apiUrl = process.env.REACT_APP_FLIGHTPLAN_API_URL;
  const apiKey = process.env.REACT_APP_FLIGHTPLAN_API_KEY;

  const config = {
    method: 'post',
    url: `${apiUrl}/flightplan/update_flight_plan`,
    headers: {
      'x-api-key': apiKey
    },
    data: plan,
  };

  const data = axios(config)
    .then(response => {
      // console.log(JSON.stringify(response.data));
      return response.data;
    })
    .catch(e => {
      console.log(`Failed to update_flight_plan(${plan.flightplan_id}:${plan.customer_code}:${plan.flight_no}).`, e);
      throw e;
    });

  return data;
};

//計画経路非表示化API実行
const _disableFlightPlan = async (flightPlan) => {

  const apiUrl = process.env.REACT_APP_FLIGHTPLAN_API_URL;
  const apiKey = process.env.REACT_APP_FLIGHTPLAN_API_KEY;

  const config = {
    method: 'post',
    url: `${apiUrl}/flightplan/disable_flight_plan?flightplan_id=${flightPlan.flightPlanId}`,
    headers: { 
      'x-api-key': apiKey
    },
  };

  const data = axios(config)
    .then(response => {
      // console.log(JSON.stringify(response.data));
      return response.data;
    })
    .catch(e => {
      console.log(`Failed to disable_flight_plan(${flightPlan.flightPlanId}:${flightPlan.customerCode}:${flightPlan.flightNo}).`, e);
      throw e;
    });

  return data;
};

const _initFlightPlan = (data = {}, subData = {}) => {
  const props = _makeProps(data);
  const toTime = (timeStr) => {
    let dt;
    if (timeStr) {
      dt = new WRAP.Core.DateTime(timeStr);
    }
    return dt ? dt.date.getTime() : undefined;
  };
  const routeList = [
    {
      type: "dep", 
      name: props.depPointName || "", 
      coordinate: [null, null], 
      alt: 0, 
      transitTime: toTime(props.etd), 
      props, 
      refs: {}
    },
    {
      type: "arr", 
      name: props.arrPointName || "", 
      coordinate: [null, null], 
      alt: 0, 
      transitTime: props.eta ? toTime(props.eta) : toTime(props.sta), 
      props, 
      refs: {}
    },
  ];
  const blankPlan = {
    ...props,
    atd: null,
    ata: null,
    depAirportIcao: null,
    depAirportIata: null,
    arrAirportIcao: null,
    arrAirportIata: null,
    etdLt: null,
    staLt: null,
    etaLt: null,
    atdLt: null,
    ataLt: null,
    scheduledRoute: null,
    routeList: routeList,
    isLinksPlan: true,
    creating: true,
    ...subData
  };
  return {...blankPlan};
};

const _makeProps = (data = {}) => {
  const defines = [
    {key: 'flightPlanId', default: null},
    {key: 'flightNo', default: null},
    {key: 'flightPlanName', default: null},
    {key: 'customerCode', default: null},
    {key: 'flightPlanUpdateTime', default: null},
    {key: 'aircraftCode', default: null},
    {key: 'flightType', default: null},
    {key: 'skd', default: null},
    {key: 'depPointName', default: null},
    {key: 'arrPointName', default: null},
    {key: 'etd', default: null},
    {key: 'eta', default: null},
    {key: 'sta', default: null},
  ];
  const props = {};
  defines.forEach((d) => {
    const value = getProperty(data, d.path || d.key);
    props[d.key] = value === undefined ? d.default : value;
  });
  return props;
};

const _makeModelApiOption = (validData, point, adjust, level=null) => {
  if (!validData.valid_times || validData.valid_times.length === 0) {
    return null;
  }
  const validTimes = [...validData.valid_times];
  validTimes.sort();
  const firstValid = parseUTCDate(validTimes[0]).getTime();
  const lastValid = parseUTCDate(validTimes[validTimes.length - 1]).getTime();
  let diff = 0;
  if (validTimes.length > 1) {
    const nextValid = parseUTCDate(validTimes[1]).getTime();
    diff = nextValid - firstValid;
  }

  const transitTime = point.transitTime + adjust * 86400 * 1000;
  const firstThreshold = firstValid - diff/2;
  const lastThreshold = lastValid + diff/2;
  if (firstThreshold > transitTime || lastThreshold < transitTime) {
    return null;
  }
  const lon = to360degreesRange(point.coordinate[0]);
  return {
    data: validData.data,
    basetime: validData.basetime,
    validTime: nearValidTime(validTimes, transitTime),
    lon: lon,
    lat: point.coordinate[1],
    level: level === null ? _getLevel(point.alt) : level
  };
};

const _getLevel = alt => {
  let level = 0;
  if (49000 <= alt && alt < 55000) {
    level = 100;  // FL530
  } else if (42000 <= alt && alt < 49000) {
    level = 150;  // FL450
  } else if (37000 <= alt && alt < 42000) {
    level = 200;  // FL390
  } else if (32000 <= alt && alt < 37000) {
    level = 250;  // FL340
  } else if (27000 <= alt && alt < 32000) {
    level = 300;  // FL300
  } else if (21000 <= alt && alt < 27000) {
    level = 500;  // FL180
  } else if (14000 <= alt && alt < 21000) {
    level = 600;  // FL140
  } else if (8500 <= alt && alt < 14000) {
    level = 700;  // FL100
  } else if (6000 <= alt && alt < 8500) {
    level = 800;  // FL70
  } else if (4000 <= alt && alt < 6000) {
    level = 850;  // FL50
  } else if (2750 <= alt && alt < 4000) {
    level = 900;  // FL30
  } else if (2250 <= alt && alt < 2750) {
    level = 925;  // FL25
  } else if (1500 <= alt && alt < 2250) {
    level = 950;  // FL20
  } else if (750 <= alt && alt < 1500) {
    level = 975;  // FL10
  } else if (0 < alt && alt < 750) {
    level = 1000; // FL5
  }
  return level;
};

export const doPlanDataUpdateState = atom({
  key: 'doPlanDataUpdateState',
  default: false
});

export const planDataRevisionState = atom({
  key: 'planDataRevisionState',
  default: 0
});
